import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
    ArrowLeft, CreditCard, Calendar, Gift, Gauge, ShieldAlert,
    Repeat, Bell, RefreshCcw,
} from "lucide-react";

/**
 * /cards/intelligence — VAULT Card Intelligence.
 *
 * Read-mostly aggregator across cards, statements, payments and the
 * new autopay/charge-watch collections. Tabs mirror the P0 spec §1–§13.
 */
const DATA_COLORS = {
    cobalt:  "#2450b8",
    emerald: "#118a5a",
    aqua:    "#0aa8b0",
    violet:  "#5b3ba8",
    coral:   "#c74f4f",
    amber:   "#b88300",
};

function Metric({ label, value, hint, color = "#0b1e3f", testid }) {
    return (
        <div data-testid={testid} className="rounded-lg border border-stone-200 bg-white p-4 min-w-[140px]">
            <div className="text-[10.5px] uppercase tracking-widest text-stone-500">{label}</div>
            <div className="font-['Fraunces',serif] text-2xl mt-1" style={{ color }}>{value}</div>
            {hint && <div className="text-[11px] text-stone-500 mt-0.5">{hint}</div>}
        </div>
    );
}

function Row({ children, testid }) {
    return <div data-testid={testid} className="flex flex-wrap gap-3">{children}</div>;
}

function Tab({ label, active, onClick, testid, Icon }) {
    return (
        <button data-testid={testid} onClick={onClick}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full transition ${
                active ? "bg-[#0b1e3f] text-[#f6f3ec]"
                       : "text-stone-700 border border-stone-200 hover:bg-stone-100"}`}>
            <Icon size={13} /> {label}
        </button>
    );
}

function fmt(n) {
    if (n == null) return "—";
    if (typeof n !== "number") return n;
    return "₹" + Math.round(n).toLocaleString("en-IN");
}

const PRIORITY_STYLE = {
    critical: { bg: "bg-rose-50",    fg: "text-rose-700",    b: "border-rose-200" },
    high:     { bg: "bg-amber-50",   fg: "text-amber-700",   b: "border-amber-200" },
    medium:   { bg: "bg-sky-50",     fg: "text-sky-700",     b: "border-sky-200" },
    low:      { bg: "bg-stone-100",  fg: "text-stone-700",   b: "border-stone-200" },
};

export default function CardIntelligence() {
    const [tab, setTab] = useState("command");
    const [dash, setDash] = useState(null);
    const [payDay, setPayDay] = useState(null);
    const [perks, setPerks] = useState(null);
    const [usage, setUsage] = useState(null);
    const [charge, setCharge] = useState(null);
    const [emi, setEmi] = useState(null);
    const [autopay, setAutopay] = useState(null);
    const [rewards, setRewards] = useState(null);
    const [attention, setAttention] = useState(null);

    const loadAll = async () => {
        const q = (p) => api.get(p).then(r => r.data).catch(() => null);
        const [c1, c2, c3, c4, c5, c6, c7, c8, c9] = await Promise.all([
            q("/api/card-intel/command-centre"),
            q("/api/card-intel/pay-day"),
            q("/api/card-intel/perks"),
            q("/api/card-intel/usage-radar"),
            q("/api/card-intel/charge-watch"),
            q("/api/card-intel/emi"),
            q("/api/card-intel/autopay"),
            q("/api/card-intel/reward-expiry"),
            q("/api/card-intel/needs-attention"),
        ]);
        setDash(c1); setPayDay(c2); setPerks(c3); setUsage(c4);
        setCharge(c5); setEmi(c6); setAutopay(c7); setRewards(c8); setAttention(c9);
    };

    useEffect(() => { loadAll(); }, []);

    const setState = async (idHint, state) => {
        try {
            await api.post(`/api/card-intel/charge-watch/${encodeURIComponent(idHint)}/state`,
                { state });
            toast.success(`Marked ${state}`);
            const c = await api.get("/api/card-intel/charge-watch").then(r => r.data);
            setCharge(c);
        } catch { toast.error("Update failed"); }
    };

    return (
        <div data-testid="card-intel" className="min-h-screen bg-[#f6f3ec] text-[#0b1e3f]">
            <div className="max-w-6xl mx-auto p-6">
                <div className="flex items-center gap-3 mb-6">
                    <Link to="/cards" className="text-stone-500 hover:text-stone-800"><ArrowLeft size={18} /></Link>
                    <div>
                        <div className="text-[10.5px] uppercase tracking-widest text-[#a0894a]">Card Intelligence</div>
                        <h1 className="font-['Fraunces',serif] text-2xl">One place for everything your cards do</h1>
                    </div>
                    <Button variant="outline" size="sm" className="ml-auto" onClick={loadAll}>
                        <RefreshCcw size={14} className="mr-1" /> Refresh
                    </Button>
                </div>

                <div className="flex flex-wrap gap-2 mb-5">
                    <Tab label="Command Centre"  active={tab==="command"}    onClick={()=>setTab("command")}    testid="ci-tab-command"    Icon={CreditCard} />
                    <Tab label="Pay Day"         active={tab==="payday"}     onClick={()=>setTab("payday")}     testid="ci-tab-payday"     Icon={Calendar} />
                    <Tab label="Usage Radar"     active={tab==="usage"}      onClick={()=>setTab("usage")}      testid="ci-tab-usage"      Icon={Gauge} />
                    <Tab label="Perks"           active={tab==="perks"}      onClick={()=>setTab("perks")}      testid="ci-tab-perks"      Icon={Gift} />
                    <Tab label="Rewards Expiry"  active={tab==="rewards"}    onClick={()=>setTab("rewards")}    testid="ci-tab-rewards"    Icon={Gift} />
                    <Tab label="Autopay Watch"   active={tab==="autopay"}    onClick={()=>setTab("autopay")}    testid="ci-tab-autopay"    Icon={Repeat} />
                    <Tab label="Charge Watch"    active={tab==="charge"}     onClick={()=>setTab("charge")}     testid="ci-tab-charge"     Icon={ShieldAlert} />
                    <Tab label="EMI"             active={tab==="emi"}        onClick={()=>setTab("emi")}        testid="ci-tab-emi"        Icon={Repeat} />
                    <Tab label="Needs Attention" active={tab==="attention"}  onClick={()=>setTab("attention")}  testid="ci-tab-attention"  Icon={Bell} />
                </div>

                {tab === "command" && dash && (
                    <div data-testid="ci-command" className="space-y-3">
                        <Row testid="ci-command-metrics">
                            <Metric label="Cards"          value={dash.counts.active_cards}      hint={`${dash.counts.total_cards} total`} testid="ci-m-cards" />
                            <Metric label="Combined limit" value={fmt(dash.combined.credit_limit)} color={DATA_COLORS.cobalt} testid="ci-m-limit" />
                            <Metric label="Used"           value={fmt(dash.combined.used)}       color={DATA_COLORS.coral}  hint={`${dash.combined.utilisation_pct}%`} testid="ci-m-used" />
                            <Metric label="Available"      value={fmt(dash.combined.available)}  color={DATA_COLORS.emerald} testid="ci-m-avail" />
                            <Metric label="Due this week"  value={dash.due_this_week.length}     color={DATA_COLORS.amber} testid="ci-m-due-week" />
                            <Metric label="Autopay on"     value={dash.counts.autopay_enabled}   color={DATA_COLORS.aqua} testid="ci-m-autopay" />
                            <Metric label="Statements ready"     value={dash.counts.statement_ready}    testid="ci-m-ready" />
                            <Metric label="Awaiting import"      value={dash.counts.statements_awaiting_import} color={DATA_COLORS.violet} testid="ci-m-awaiting" />
                            <Metric label="Needs attention"      value={dash.counts.needs_attention}    color={DATA_COLORS.coral} testid="ci-m-attention" />
                        </Row>
                    </div>
                )}

                {tab === "payday" && payDay && (
                    <div data-testid="ci-payday" className="space-y-4">
                        {[["today","Today"],["next_7","Next 7 days"],["next_30","Next 30 days"]].map(([k, label])=>(
                            <Card key={k}>
                                <CardHeader><CardTitle className="text-base font-['Fraunces',serif]">{label}</CardTitle></CardHeader>
                                <CardContent>
                                    {payDay[k].length === 0
                                        ? <div className="text-sm text-stone-500">Nothing due.</div>
                                        : (
                                            <table className="w-full text-sm">
                                                <tbody>
                                                    {payDay[k].map(r=>(
                                                        <tr key={r.event_id} className="border-b border-stone-100">
                                                            <td className="py-2 pr-3 font-medium">{r.card_name}</td>
                                                            <td className="py-2 pr-3 text-stone-600">{r.due_date}</td>
                                                            <td className="py-2 pr-3">Due {fmt(r.remaining)}</td>
                                                            <td className="py-2 pr-3 text-stone-500">Min {fmt(r.min_amount_due)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                </CardContent>
                            </Card>
                        ))}
                        <div className="text-xs text-stone-500">{payDay.pay_selected_note}</div>
                    </div>
                )}

                {tab === "usage" && usage && (
                    <div data-testid="ci-usage" className="space-y-3">
                        <Row><Metric label="Portfolio limit" value={fmt(usage.portfolio.limit)} color={DATA_COLORS.cobalt} testid="ci-usage-total" />
                            <Metric label="Used"      value={fmt(usage.portfolio.used)}      color={DATA_COLORS.coral} hint={`${usage.portfolio.pct}%`} />
                            <Metric label="Available" value={fmt(usage.portfolio.available)} color={DATA_COLORS.emerald} /></Row>
                        <Card>
                            <CardHeader><CardTitle className="text-base font-['Fraunces',serif]">Per card</CardTitle></CardHeader>
                            <CardContent>
                                {usage.per_card.map(c=>(
                                    <div key={c.card_id} className="flex items-center gap-3 py-2 border-b border-stone-100 last:border-0">
                                        <div className="w-40 truncate font-medium">{c.card_name}</div>
                                        <div className="flex-1 h-2 rounded-full bg-stone-100 overflow-hidden">
                                            <div className="h-full rounded-full"
                                                 style={{ width: `${Math.min(c.pct, 100)}%`,
                                                          background: c.pct > 70 ? DATA_COLORS.coral :
                                                                       c.pct > 40 ? DATA_COLORS.amber : DATA_COLORS.emerald }} />
                                        </div>
                                        <div className="w-20 text-right text-stone-600 text-sm">{c.pct}%</div>
                                        <div className="w-32 text-right text-stone-500 text-sm">{fmt(c.used)} / {fmt(c.limit)}</div>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                        <div className="text-xs text-stone-500">{usage.note}</div>
                    </div>
                )}

                {tab === "perks" && perks && (
                    <Card data-testid="ci-perks">
                        <CardHeader><CardTitle className="text-base font-['Fraunces',serif]">Perks per card</CardTitle></CardHeader>
                        <CardContent>
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-[10.5px] uppercase tracking-widest text-stone-500 border-b border-stone-200">
                                        <th className="py-2 pr-3">Card</th>
                                        <th className="py-2 pr-3">Reward</th>
                                        <th className="py-2 pr-3">Annual fee</th>
                                        <th className="py-2 pr-3">Forex</th>
                                        <th className="py-2 pr-3">Best for</th>
                                        <th className="py-2 pr-3">Balance</th>
                                        <th className="py-2 pr-3">Expiry</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {perks.cards.map(c=>(
                                        <tr key={c.card_id} className="border-b border-stone-100">
                                            <td className="py-2 pr-3 font-medium">{c.card_name || c.bank_name}</td>
                                            <td className="py-2 pr-3">{c.reward_program || "—"}</td>
                                            <td className="py-2 pr-3">{fmt(c.annual_fee)}</td>
                                            <td className="py-2 pr-3">{c.forex_markup ? `${c.forex_markup}%` : "—"}</td>
                                            <td className="py-2 pr-3">{c.best_usage_category || "—"}</td>
                                            <td className="py-2 pr-3">{c.reward_balance || "—"}</td>
                                            <td className="py-2 pr-3">{c.reward_expiry_date || "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div className="text-xs text-stone-500 mt-3">{perks.note}</div>
                        </CardContent>
                    </Card>
                )}

                {tab === "rewards" && rewards && (
                    <div data-testid="ci-rewards" className="space-y-3">
                        {[["in_7_days","in the next 7 days","#c74f4f"],
                          ["in_15_days","in the next 15 days","#b88300"],
                          ["in_30_days","in the next 30 days","#0aa8b0"]].map(([k, label, color])=>(
                            <Card key={k}>
                                <CardHeader><CardTitle className="text-base font-['Fraunces',serif]" style={{ color }}>Expiring {label}</CardTitle></CardHeader>
                                <CardContent>
                                    {rewards[k].length === 0
                                        ? <div className="text-sm text-stone-500">Nothing expiring in this window.</div>
                                        : rewards[k].map(r=>(
                                            <div key={r.card_id} className="flex justify-between text-sm py-1">
                                                <span>{r.card_name}</span>
                                                <span className="text-stone-500">{r.amount || r.balance} pts · {r.days}d</span>
                                            </div>))}
                                </CardContent>
                            </Card>
                        ))}
                        <div className="text-xs text-stone-500">{rewards.note}</div>
                    </div>
                )}

                {tab === "autopay" && autopay && (
                    <Card data-testid="ci-autopay">
                        <CardHeader><CardTitle className="text-base font-['Fraunces',serif]">Autopay status</CardTitle></CardHeader>
                        <CardContent>
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-[10.5px] uppercase tracking-widest text-stone-500 border-b border-stone-200">
                                        <th className="py-2 pr-3">Card</th>
                                        <th className="py-2 pr-3">Enabled</th>
                                        <th className="py-2 pr-3">Mode</th>
                                        <th className="py-2 pr-3">Source</th>
                                        <th className="py-2 pr-3">Last confirmed</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {autopay.rows.map(r=>(
                                        <tr key={r.card_id} className="border-b border-stone-100">
                                            <td className="py-2 pr-3 font-medium">{r.card_name}</td>
                                            <td className="py-2 pr-3">{r.enabled ? "Yes" : "No"}</td>
                                            <td className="py-2 pr-3">{r.mode || "—"}</td>
                                            <td className="py-2 pr-3">{r.payment_source || "—"}</td>
                                            <td className="py-2 pr-3 text-stone-500">{r.last_confirmed_at || "Never"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div className="text-xs text-stone-500 mt-3">{autopay.note}</div>
                        </CardContent>
                    </Card>
                )}

                {tab === "charge" && charge && (
                    <div data-testid="ci-charge" className="space-y-3">
                        {Object.entries(charge.buckets).map(([state, rows])=>(
                            <Card key={state}>
                                <CardHeader><CardTitle className="text-base font-['Fraunces',serif] capitalize">{state} · {rows.length}</CardTitle></CardHeader>
                                <CardContent>
                                    {rows.length === 0
                                        ? <div className="text-sm text-stone-500">Nothing here.</div>
                                        : rows.map(r=>(
                                            <div key={r.id_hint} className="flex flex-wrap items-center gap-2 py-2 border-b border-stone-100 last:border-0">
                                                <div className="text-sm min-w-[140px]"><b>{r.card_name}</b></div>
                                                <div className="text-sm text-stone-600 flex-1 min-w-[220px]">{r.description}</div>
                                                <div className="text-sm">{fmt(r.amount)}</div>
                                                {state === "review" && (<>
                                                    <Button size="sm" variant="outline" onClick={()=>setState(r.id_hint,"expected")}>Expected</Button>
                                                    <Button size="sm" variant="outline" onClick={()=>setState(r.id_hint,"linked")}>Link</Button>
                                                    <Button size="sm" variant="outline" onClick={()=>setState(r.id_hint,"ignored")}>Ignore</Button>
                                                    <Button size="sm" variant="outline" onClick={()=>setState(r.id_hint,"investigating")}>Investigate</Button>
                                                </>)}
                                            </div>))}
                                </CardContent>
                            </Card>
                        ))}
                        <div className="text-xs text-stone-500">{charge.note}</div>
                    </div>
                )}

                {tab === "emi" && emi && (
                    <Card data-testid="ci-emi">
                        <CardHeader><CardTitle className="text-base font-['Fraunces',serif]">EMIs in flight</CardTitle></CardHeader>
                        <CardContent>
                            {emi.rows.length === 0
                                ? <div className="text-sm text-stone-500">No EMIs on file.</div>
                                : (
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-[10.5px] uppercase tracking-widest text-stone-500 border-b border-stone-200">
                                                <th className="py-2 pr-3">Merchant</th>
                                                <th className="py-2 pr-3">Original</th>
                                                <th className="py-2 pr-3">Monthly</th>
                                                <th className="py-2 pr-3">Remaining</th>
                                                <th className="py-2 pr-3">Ends</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {emi.rows.map((r, i)=>(
                                                <tr key={i} className="border-b border-stone-100">
                                                    <td className="py-2 pr-3 font-medium">{r.merchant}</td>
                                                    <td className="py-2 pr-3">{fmt(r.original)}</td>
                                                    <td className="py-2 pr-3">{fmt(r.monthly)}</td>
                                                    <td className="py-2 pr-3">{r.remaining} / {r.tenure}</td>
                                                    <td className="py-2 pr-3 text-stone-500">{r.end_date || "—"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            <div className="text-xs text-stone-500 mt-3">{emi.note}</div>
                        </CardContent>
                    </Card>
                )}

                {tab === "attention" && attention && (
                    <Card data-testid="ci-attention">
                        <CardHeader><CardTitle className="text-base font-['Fraunces',serif]">Needs attention · {attention.count}</CardTitle></CardHeader>
                        <CardContent>
                            {attention.events.length === 0
                                ? <div className="text-sm text-stone-500">Nothing needs attention right now.</div>
                                : attention.events.map((e,i)=>{
                                    const s = PRIORITY_STYLE[e.priority] || PRIORITY_STYLE.low;
                                    return (
                                        <div key={i} className={`flex items-center gap-3 py-2 px-3 rounded-md border ${s.b} ${s.bg} mb-2`}>
                                            <span className={`text-[10px] font-semibold uppercase tracking-widest ${s.fg}`}>{e.priority}</span>
                                            <span className="font-medium min-w-[140px]">{e.card_name}</span>
                                            <span className="text-sm text-stone-700 flex-1">{e.detail}</span>
                                        </div>);
                                })}
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    );
}
